import React, { useState } from "react";
import { format } from "date-fns";
import {
  Calendar,
  AlertTriangle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Boxes,
  Zap,
  ShoppingCart,
  CheckCircle2,
  Clock,
  Package,
} from "lucide-react";
import { cn, formatNumber } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MaterialIdentityCell } from "../MaterialIdentityCell";
import {
  groupMaterialsByExpectedDate,
  MaterialDateGroup,
  FlattenedMaterialItem,
} from "../utils/materialPlanningGroupUtils";

interface MaterialDemandDailyGroupedViewProps {
  pos: any[];
  isLoading?: boolean;
  onQuickAllocateOrIssue: (poId: string, materialPlanIds?: string[]) => void;
  onOpenPRForm: (item: any) => void;
  onNavigateToOrder: (poCode: string) => void;
}

export function MaterialDemandDailyGroupedView({
  pos,
  isLoading = false,
  onQuickAllocateOrIssue,
  onOpenPRForm,
  onNavigateToOrder,
}: MaterialDemandDailyGroupedViewProps) {
  // Gom nhóm vật tư theo ngày cần
  const groups: MaterialDateGroup[] = React.useMemo(() => {
    return groupMaterialsByExpectedDate(pos);
  }, [pos]);

  // State đóng mở từng ngày
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());

  const toggleCollapse = (dateKey: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="h-48 flex items-center justify-center gap-2 text-slate-500 bg-white rounded-xl border border-slate-200">
        <Clock className="w-5 h-5 animate-spin text-teal-600" />
        <span>Đang tính toán nhu cầu vật tư theo ngày...</span>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="h-48 flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-slate-200 text-slate-500">
        <Boxes className="w-8 h-8 text-slate-300" />
        <span className="font-semibold text-sm">Không tìm thấy nhu cầu vật tư phù hợp bộ lọc</span>
        <span className="text-xs text-muted-foreground">Thử đổi từ khóa tìm kiếm hoặc bỏ lọc trạng thái</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const isCollapsed = collapsedDates.has(group.dateKey);
        const sub = group.subtotal;

        return (
          <div
            key={group.dateKey}
            className="rounded-xl border border-slate-200 bg-white shadow-2xs overflow-hidden"
          >
            {/* Header Khối Ngày Nhu Cầu Vật Tư */}
            <div
              data-group-row=""
              onClick={() => toggleCollapse(group.dateKey)}
              className={cn(
                "px-4 py-2.5 flex items-center justify-between gap-3 border-b cursor-pointer transition-colors select-none overflow-x-auto whitespace-nowrap",
                group.dateKey === "NO_DATE"
                  ? "bg-slate-100 border-slate-200 text-slate-700"
                  : "bg-teal-50/80 hover:bg-teal-100/70 border-teal-200 text-teal-900"
              )}
            >
              <div className="flex items-center gap-3 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-600 hover:text-teal-700"
                >
                  {isCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-700" />
                  <span className="font-bold text-sm text-slate-900 tracking-tight">
                    {group.displayDate}
                  </span>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/90 border border-teal-200 text-teal-800 shadow-2xs">
                  {sub.totalItems} vật tư
                </span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 shadow-2xs">
                  📦 {sub.totalPOs} lệnh sản xuất
                </span>
                {sub.totalShortage > 0 && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-700 flex items-center gap-1 animate-pulse">
                    <AlertTriangle className="w-3 h-3 text-rose-600" />
                    Thiếu: {formatNumber(sub.totalShortage)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 text-xs font-mono shrink-0">
                {sub.isMixedUnits && (
                  <span
                    className="text-[10px] font-sans text-amber-800 bg-amber-100/90 px-1.5 py-0.5 rounded border border-amber-300"
                    title={Object.values(sub.byUnit)
                      .map((b: any) => `${formatNumber(b.qtyRequired)} ${b.unit}`)
                      .join(" • ")}
                  >
                    Đa ĐVT ({Object.keys(sub.byUnit).length})
                  </span>
                )}
                <span className="text-slate-600 font-medium">
                  Cần: <strong className="text-slate-900">{formatNumber(sub.totalRequired)}</strong>
                </span>
                <span className="text-teal-700 font-medium">
                  Tồn: <strong className="text-teal-800">{formatNumber(sub.totalAvailable)}</strong>
                </span>
                <span
                  className={cn(
                    "px-2 py-0.5 rounded border font-semibold",
                    sub.fulfillmentPercent === 100
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-amber-50 border-amber-200 text-amber-800"
                  )}
                >
                  Đáp ứng: {sub.fulfillmentPercent}%
                </span>
              </div>
            </div>

            {/* Bảng Chi Tiết Vật Tư Cần Trong Ngày */}
            {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse min-w-[1180px]">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 select-none">
                    <tr>
                      <th
                        className="px-3 py-2 text-left font-bold w-[160px] sticky left-0 z-30 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                        style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                      >
                        Mã LSX & Đơn Hàng
                      </th>
                      <th className="px-3 py-2 text-left font-bold w-[200px]">Khách Hàng & Sản Phẩm</th>
                      <th className="px-3 py-2 text-left font-bold w-[260px]">Loại • Mã & Tên Vật Tư</th>
                      <th className="px-3 py-2 text-right font-bold w-[120px]">Nhu Cầu Định Mức</th>
                      <th className="px-3 py-2 text-right font-bold w-[110px]">Tồn Khả Dụng</th>
                      <th className="px-3 py-2 text-right font-bold w-[110px]">Thiếu Hụt</th>
                      <th className="px-3 py-2 text-center font-bold w-[130px]">Trạng Thái</th>
                      <th
                        className="px-3 py-2 text-right font-bold w-[130px] sticky right-0 z-30 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                        style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                      >
                        Thao Tác
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {group.items.map((mat: FlattenedMaterialItem, idx: number) => {
                      const isEven = idx % 2 === 0;
                      const solidRowBg = isEven ? "#ffffff" : "#f8fafc";
                      const isShortage = mat.shortageQty > 0;
                      const isReady = mat.status === "READY" || (mat.qtyAvailable >= mat.qtyRequired && mat.qtyRequired > 0);

                      return (
                        <tr
                          key={`${mat.poId}_${mat.id}_${idx}`}
                          className="hover:bg-teal-50/40 transition-colors group cursor-pointer"
                          onClick={() => {
                            if (window.getSelection()?.toString().trim()) return;
                            if (mat.poCode) onNavigateToOrder(mat.poCode);
                          }}
                        >
                          {/* Sticky Left: PO Code & Sales Order */}
                          <td
                            className="px-3 py-2 align-top sticky left-0 z-20 border-r border-slate-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                            style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                          >
                            <div className="flex flex-col gap-0.5">
                              <span className="font-mono font-bold text-teal-700 text-left">
                                {mat.poCode}
                              </span>
                              <span className="text-[10px] font-medium text-slate-500">
                                PO: {mat.poStatus}
                              </span>
                            </div>
                          </td>

                          {/* Customer & Product */}
                          <td className="px-3 py-2 align-top">
                            <div className="flex flex-col gap-0.5">
                              <span
                                className="font-semibold text-slate-800 line-clamp-1"
                                title={mat.productName}
                              >
                                {mat.productName}
                              </span>
                              <span
                                className="text-[11px] text-muted-foreground break-words whitespace-normal"
                                title={mat.customerName}
                              >
                                {mat.customerName || "Khách lẻ"}
                              </span>
                            </div>
                          </td>

                          {/* Material Identity Cell (Clean Lookups - Rule 6) */}
                          <td className="px-3 py-2 align-top">
                            <MaterialIdentityCell
                              materialCode={mat.materialCode}
                              materialName={mat.materialName}
                              category={mat.materialCategory}
                              typeLabel={mat.typeLabel}
                              suppliedSize={mat.suppliedSize}
                              unit={mat.unit}
                            />
                          </td>

                          {/* Nhu Cầu Định Mức */}
                          <td className="px-3 py-2 align-top text-right font-mono">
                            <span className="font-bold text-slate-900 text-xs">
                              {formatNumber(mat.qtyRequired)}
                            </span>{" "}
                            <span className="text-[11px] text-slate-500">{mat.unit}</span>
                          </td>

                          {/* Tồn Khả Dụng */}
                          <td className="px-3 py-2 align-top text-right font-mono">
                            <span className="text-slate-700 text-xs">
                              {formatNumber(mat.qtyAvailable)}
                            </span>{" "}
                            <span className="text-[11px] text-slate-500">{mat.unit}</span>
                          </td>

                          {/* Thiếu Hụt */}
                          <td className="px-3 py-2 align-top text-right font-mono">
                            {isShortage ? (
                              <span className="text-rose-600 font-bold flex items-center justify-end gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {formatNumber(mat.shortageQty)} {mat.unit}
                              </span>
                            ) : (
                              <span className="text-emerald-600 font-medium">0 {mat.unit}</span>
                            )}
                          </td>

                          {/* Trạng Thái */}
                          <td className="px-3 py-2 align-top text-center">
                            {isShortage ? (
                              <Badge className="bg-rose-100 text-rose-800 border-none font-bold text-[10px]">
                                ❌ THIẾU HỤT
                              </Badge>
                            ) : isReady ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-none font-bold text-[10px]">
                                ✓ ĐỦ VẬT TƯ
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800 border-none font-bold text-[10px]">
                                ⏳ CHỜ CẤP
                              </Badge>
                            )}
                          </td>

                          {/* Sticky Right: Actions */}
                          <td
                            className="px-3 py-2 align-middle text-right sticky right-0 z-20 border-l border-slate-200 shadow-[-2px_0_4px_-1px_rgba(0,0,0,0.06)]"
                            style={{ backgroundColor: solidRowBg, isolation: "isolate" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {isShortage ? (
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[11px] bg-rose-600 hover:bg-rose-700 text-white font-semibold cursor-pointer shadow-2xs"
                                  onClick={() => onOpenPRForm(mat)}
                                  title="Tạo Yêu Cầu Mua Hàng (PR) cho lượng thiếu"
                                >
                                  <ShoppingCart className="w-3 h-3 mr-1" />
                                  <span>Mua Hàng</span>
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-[11px] bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 font-semibold cursor-pointer"
                                  onClick={() => onQuickAllocateOrIssue(mat.poId, [mat.materialPlanId])}
                                  title="Cấp phát vật tư này cho lệnh sản xuất"
                                >
                                  <Zap className="w-3 h-3 mr-1 fill-emerald-600 text-emerald-600" />
                                  <span>Cấp Phát</span>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
